<script setup>
import CanvasRenderer from '@/components/CanvasRenderer.vue'
import { onMounted, computed, ref } from 'vue'
import { useCanvasStore } from '@/store/canvas'
import { useInstanceStore } from '@/store/backendInstances.js'
import { useDisplay } from 'vuetify'
import { GeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup.ts'
import GenericInfluxChart from '@/composables/charts/GenericInfluxChart.vue'
import GenericTimeSeriesChart from '@/composables/charts/GenericTimeSeriesChart.vue'
import { ANNOTATOR_TYPES } from '@/composables/charts/influx-charts/ChartIncidentAnnotators.ts'
import BarnEquipmentRegionSumChart from '@/composables/charts/quickCharts/BarnEquipmentRegionSumChart.vue'
import { InfluxLineChartBuilder } from '@/composables/charts/influx-charts/chart-builders/InfluxLineChartBuilder.ts'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES.ts'
import { RegionSumInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionSumChartBuilder.ts'
import { IncidentBarChartBuilder } from '@/composables/charts/influx-charts/chart-builders/IncidentBarChartBuilder.ts'
import { DateTime } from 'luxon'

const incidentAnnotators = [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }]
const instanceStore = useInstanceStore()
const canvasStore = useCanvasStore()
const { mobile } = useDisplay()

const timestamp = ref(DateTime.now().startOf('second'))

const baseCanvas = computed(() => {
  return canvasStore.getBaseCanvas()
})

const canSeeRegionSums = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.regionSums'])
})
const canSeeActivity = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.activity'])
})
const canSeeSpeed = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.speed'])
})

onMounted(() => {
  timestamp.value = DateTime.now().startOf('second')
  canvasStore.keepBaseCanvasLoaded()
})

const isVerticalbarn = computed(() => {
  return baseCanvas.value?.heightRatio < 2.5
})
</script>

<template>
  <v-row class="ma-0">
    <v-col :cols="12">
      <!-- we need overflow-visible here, because when opening the plugins they are overflowing the canvas -->
      <v-glass-card
        class="pa-0 overflow-visible"
        :min-height="isVerticalbarn ? '50vh' : '400px'"
        height="100%"
        style="z-index: 1"
      >
        <CanvasRenderer
          v-if="baseCanvas"
          :full-height="!mobile"
          :disable-zoom-buttons="false"
          :override-canvas="baseCanvas"
          :model-value:base-rotation="270"
          :disable-element-picker="false"
          :disable-time-jumper="false"
          :disable-navigation="false"
          :draggable="!mobile"
          :pinchable="mobile"
          :scrollable="false"
          :ctrl-scrollable="!mobile"
          :shrink-all-overlays-by-default="true"
          :timestamp="timestamp"
        />
      </v-glass-card>
    </v-col>
    <v-col
      v-if="canSeeActivity"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new InfluxLineChartBuilder({
            chartType: CHART_TYPES.LINE,
            measurement: 'activity',
            field: 'activity',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement').serialize(),
            title: $t('charts.titles.activity'),
            subtitle: $t('charts.subtitles.activityGlobal'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
          })
        "
      ></GenericInfluxChart>
    </v-col>
    <v-col
      v-if="canSeeRegionSums"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionSumInfluxChartBuilder({
            chartType: CHART_TYPES.REGION_SUMS,
            field: 'meanCount',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement').serialize(),
            title: $t('charts.titles.labelCounts'),
            subtitle: $t('charts.subtitles.labelCountsGlobal'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
            regionSumProperty: 'label',
            regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: true },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionSums"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionSumInfluxChartBuilder({
            chartType: CHART_TYPES.REGION_SUMS,
            field: 'meanCount',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement').serialize(),
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsFrontBack'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
            regionSumProperty: 'stallbereich_vh',
            regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: true },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionSums"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionSumInfluxChartBuilder({
            chartType: CHART_TYPES.REGION_SUMS,
            field: 'meanCount',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement').serialize(),
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
            regionSumProperty: 'stalltechnik',
            regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: true },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeSpeed"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new InfluxLineChartBuilder({
            chartType: CHART_TYPES.LINE,
            measurement: 'speed',
            field: 'speed',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement').serialize(),
            title: $t('charts.titles.speed'),
            subtitle: $t('charts.subtitles.speedGlobal'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col :cols="12">
      <GenericTimeSeriesChart
        :chart-builder="
          new IncidentBarChartBuilder({
            chartType: CHART_TYPES.INCIDENT_BAR,
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement', 1000 * 60 * 60 * 6).serialize(),
            title: $t('charts.titles.deadAnimals'),
            subtitle: $t('charts.subtitles.deadAnimals'),
            incidentTypes: ['deadAnimal'],
            timestampMode: 'opened',
            additionalFilters: [
              { key: 'hidden', operator: '==', value: false },
              { key: 'messagePayload.lastDetection.regions.stallbereich_lr', operator: 'exists', value: true },
            ],
          })
        "
      ></GenericTimeSeriesChart>
    </v-col>

    <v-divider
      thickness="4"
      class="my-4"
    ></v-divider>
    <v-col
      v-if="canSeeActivity"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new InfluxLineChartBuilder({
            chartType: CHART_TYPES.LINE,
            measurement: 'activity',
            field: 'activity',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement', 1000 * 60 * 60 * 12).serialize(),
            title: $t('charts.titles.activity'),
            subtitle: $t('charts.subtitles.activityGlobal'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
          })
        "
      ></GenericInfluxChart>
    </v-col>
    <v-col
      v-if="canSeeRegionSums"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionSumInfluxChartBuilder({
            chartType: CHART_TYPES.REGION_SUMS,
            field: 'meanCount',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement', 1000 * 60 * 60 * 12).serialize(),
            title: $t('charts.titles.labelCounts'),
            subtitle: $t('charts.subtitles.labelCountsGlobal'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
            regionSumProperty: 'label',
            regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: true },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionSums"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionSumInfluxChartBuilder({
            chartType: CHART_TYPES.REGION_SUMS,
            field: 'meanCount',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement', 1000 * 60 * 60 * 12).serialize(),
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsFrontBack'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
            regionSumProperty: 'stallbereich_vh',
            regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: true },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionSums"
      :cols="12"
    >
      <BarnEquipmentRegionSumChart
        synchronization-key="barnManagement"
        :fixed-time-resolution="1000 * 60 * 60 * 12"
      ></BarnEquipmentRegionSumChart>
    </v-col>

    <v-col
      v-if="canSeeSpeed"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new InfluxLineChartBuilder({
            chartType: CHART_TYPES.LINE,
            measurement: 'speed',
            field: 'speed',
            xAggregation: 'mean',
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement', 1000 * 60 * 60 * 12).serialize(),
            title: $t('charts.titles.speed'),
            subtitle: $t('charts.subtitles.speedGlobal'),
            incidentAnnotators: incidentAnnotators,
            incidentReducer: { reducerType: 'penState' },
          })
        "
      ></GenericInfluxChart>
    </v-col>
  </v-row>
</template>
